// 測定セッションのタイマー会計と実測サマリ（§14）。すべて決定的な純関数（MC なし）。
// now は Date.now を引数注入（テスト容易性・walltime ベースでタブ非アクティブでも正確）。
import { rowBaseValue, toBase } from '../currency/convert';
import type { RateTable } from '../currency/rate-table';
import { expectedIncome } from '../core/engine';
import { resolveRate } from '../core/rate-model';
import { runsPerHour } from './derive';
import { expandSet, memberWeight } from './expand';
import { isGroupDrop } from '../store/schema';
import type { FarmSet } from '../store/schema';
import type { MeasureDropCounter, MeasureSession } from '../store/measure-schema';

// ── タイマー会計 ──────────────────────

/** 実効経過 ms（一時停止を除く）。走行中は runningSince からの分を加算。 */
export function effectiveElapsedMs(
  s: Pick<MeasureSession, 'elapsedMs' | 'runningSince'>,
  now: number,
): number {
  return s.elapsedMs + (s.runningSince != null ? Math.max(0, now - s.runningSince) : 0);
}

export function isRunning(s: Pick<MeasureSession, 'runningSince'>): boolean {
  return s.runningSince != null;
}

export function startTimer(s: MeasureSession, now: number): void {
  if (s.runningSince != null) return;
  s.runningSince = now;
  s.lastTickAt = now;
}

export function pauseTimer(s: MeasureSession, now: number): void {
  if (s.runningSince == null) return;
  s.elapsedMs += Math.max(0, now - s.runningSince);
  s.runningSince = null;
  s.lastTickAt = null;
}

/** 走行中の heartbeat（クラッシュ復元時の上限時刻）。呼び出し側が定期保存とセットで使う。 */
export function heartbeat(s: MeasureSession, now: number): void {
  if (s.runningSince == null) return;
  s.lastTickAt = now;
}

/**
 * リロード復元: 走行中のまま保存されていたら、最後に観測できた時刻（lastTickAt）までを
 * elapsedMs に畳んで一時停止状態にする。ブラウザを閉じていた時間は計上しない。
 */
export function restoreSession(s: MeasureSession): void {
  if (s.runningSince == null) {
    s.lastTickAt = null;
    return;
  }
  const upto = s.lastTickAt ?? s.runningSince;
  s.elapsedMs += Math.max(0, upto - s.runningSince);
  s.runningSince = null;
  s.lastTickAt = null;
}

// ── 実測サマリ ──────────────────────

export interface MeasureDropSummary {
  counterId: string;
  name: string;
  /** 観測個数 k。 */
  count: number;
  /** 実測レート k/N（N=0 は null）。 */
  measuredLambda: number | null;
  /** リンクセットの想定 λ（対応行あり時のみ）。 */
  expectedLambda: number | null;
  /** このカウンタの収入（base）。レート未取得は null。 */
  incomeBase: number | null;
}

export interface MeasureSummary {
  /** 実効経過 ms（一時停止除く）。 */
  elapsedMs: number;
  runs: number;
  /** 実測 1周あたり秒（runs=0 は null）。 */
  avgRunSec: number | null;
  /** リンクセットの想定 1周あたり秒。 */
  expectedRunSec: number | null;
  /** 実測収入（base）= Σドロップ + 安定収入×runs（リンク時）。 */
  incomeBase: number;
  /** 実測コスト（base）= runs × セットの C（リンク時）+ Σ臨時コスト。 */
  costBase: number;
  /** 実測時給（base）= (収入 − コスト) / 実効時間。elapsed=0 は null。 */
  measuredHourlyBase: number | null;
  /** リンクセットの想定期待時給（base）= (期待収入 − C) × 3600/t。 */
  expectedHourlyBase: number | null;
  perDrop: MeasureDropSummary[];
  /** レート未取得で集計から除外した行の名前（黙って0にしない, §4）。 */
  missingRates: string[];
}

/** リンクセット内の対応行から想定 λ を引く。 */
export function expectedLambdaOf(counter: MeasureDropCounter, set: FarmSet | null): number | null {
  if (!set) return null;
  const src = counter.source;
  if (src.kind === 'adhoc') return null;
  if (src.kind === 'set-line') {
    for (const entry of set.dropLines) {
      if (!isGroupDrop(entry) && entry.id === src.lineId) {
        return entry.rateMode.kind === 'direct' ? Math.max(0, entry.count) : resolveRate(entry.rateMode).lambdaHat;
      }
    }
    return null;
  }
  // set-member: λ_i = m × w_i / Σw（§5.2.2 の正規化配分）。
  for (const entry of set.dropLines) {
    if (isGroupDrop(entry) && entry.id === src.groupId) {
      const totalW = entry.members.reduce((acc, x) => acc + memberWeight(x), 0);
      const mem = entry.members.find((x) => x.id === src.memberId);
      if (!mem || totalW <= 0) return null;
      return (Math.max(0, entry.m) * memberWeight(mem)) / totalW;
    }
  }
  return null;
}

export function summarize(
  session: MeasureSession,
  now: number,
  rt: RateTable | null,
  linkedSet: FarmSet | null,
): MeasureSummary {
  const elapsedMs = effectiveElapsedMs(session, now);
  const runs = session.runs;
  const missing: string[] = [];

  // ドロップ収入（base）。レート未取得の行は除外して名前を控える。
  let income = 0;
  const perDrop: MeasureDropSummary[] = session.drops.map((c) => {
    const per = rt ? rowBaseValue(c, rt) : null;
    const rowIncome = per == null ? null : per * c.count;
    if (rowIncome == null) {
      if (c.count > 0) missing.push(c.name || c.itemId || c.unit);
    } else {
      income += rowIncome;
    }
    return {
      counterId: c.id,
      name: c.name,
      count: c.count,
      measuredLambda: runs > 0 ? c.count / runs : null,
      expectedLambda: expectedLambdaOf(c, linkedSet),
      incomeBase: rowIncome,
    };
  });

  // リンクセットの想定（C・安定収入・期待時給）。
  let cost = 0;
  let expectedHourly: number | null = null;
  if (linkedSet && rt) {
    const { input } = expandSet(linkedSet, rt);
    cost += runs * input.C;
    income += runs * input.Sbase;
    expectedHourly = (expectedIncome(input) - input.C) * runsPerHour(linkedSet.time);
  }

  // 臨時コスト（総額）。
  for (const c of session.extraCosts) {
    const per = rt ? rowBaseValue(c, rt) : null;
    if (per == null) {
      if (c.count > 0) missing.push(c.name || c.itemId || c.unit);
      continue;
    }
    cost += per * c.count;
  }

  const hours = elapsedMs / 3_600_000;
  return {
    elapsedMs,
    runs,
    avgRunSec: runs > 0 ? elapsedMs / 1000 / runs : null,
    expectedRunSec: linkedSet ? linkedSet.time : null,
    incomeBase: income,
    costBase: cost,
    measuredHourlyBase: hours > 0 ? (income - cost) / hours : null,
    expectedHourlyBase: expectedHourly,
    perDrop,
    missingRates: missing,
  };
}

/** 臨時コスト1行の base 額（表示用）。 */
export function extraCostBase(
  c: { itemId?: string; unit: string; value: number; count: number },
  rt: RateTable | null,
): number | null {
  if (!rt) return null;
  const per = rowBaseValue(c, rt);
  return per == null ? null : per * c.count;
}

/** 安定収入の base/周（表示用）。 */
export function steadyBasePerRun(set: FarmSet, rt: RateTable): number | null {
  return toBase(set.steady.amount, set.steady.unit, rt);
}
